import type { Destination } from '../destination';
import { lazySdk, urlOf } from '../lazy-sdk';
import { titleOf, toVendorEvent } from '../mapping';

/**
 * Twilio Segment: `screen_view` is a `page` call, every other event a
 * `track` call with the row as its properties. No `identify` call is made:
 * the reader's account id is not sent to Segment. Pass-through: no insights
 * guarantee.
 */
export function createSegmentDestination(writeKey: string): Destination {
	const segment = lazySdk(() =>
		// `AnalyticsBrowser.load` is itself thenable: the chain resolves to the
		// ready `Analytics` instance once Segment's settings have arrived.
		import('@segment/analytics-next')
			.then(({ AnalyticsBrowser }) => AnalyticsBrowser.load({ writeKey }))
			.then(([analytics]) => analytics),
	);

	return {
		name: 'segment',
		start: segment.load,
		send: (event) => {
			const { name, params } = toVendorEvent(event);
			segment.use((analytics) => {
				if (event.name === 'screen_view') {
					void analytics.page({
						...params,
						url: urlOf(event.path),
						title: titleOf(event) ?? document.title,
					});
				} else {
					void analytics.track(name, params);
				}
			});
		},
	};
}
